import { memo, useState, useEffect, useContext } from 'react';
import {
  Card,
  CardMedia, 
  CardContent,
  Box,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Paper,
  Button
} from '@mui/material';
import {
  FavoriteBorder,
  Favorite,
  LocationOn,
  ChevronLeft,
  ChevronRight,
  ArrowForward
} from '@mui/icons-material';
import { getPropertyImages } from './api.js';
import { AuthContext } from './../../AuthContext/AuthContext';
import { BASE_URL } from './../../Api/ApiUrls';
import GuestLoginModal from '../../GuestLoginModal';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
  
// ─── Full Indian number format: 9430 → "9,430" ────────────────────────────────
const formatPrice = (price) => {
  const num = Number(price);
  if (!num || isNaN(num) || num <= 0) return '0';
  return num.toLocaleString('en-IN');
};

// ─── Normalize whatever the DB sends into a clean unit label ──────────────────
const normalizeUnit = (rawUnit, areaField) => {
  const u = String(rawUnit || '').trim().toLowerCase();

  if (!u || u === 's' || u === 'sq' || u.length === 1) {
    if (areaField === 'max_acres') return 'acre';
    return 'sft';
  }
  if (['acre', 'acres'].includes(u))   return 'acre';
  if (['gunta', 'guntas'].includes(u)) return 'gunta';
  if (u.includes('sq') && u.includes('ft')) return 'sft';
  if (u.includes('sq') && u.includes('m'))  return 'sqm';
  if (u === 'ft' || u === 'feet')           return 'sft';
  if (u === 'm' || u === 'meter')           return 'sqm';
  return rawUnit;
};

// ─── Extract numeric area from any property shape ─────────────────────────────
const getAreaNumber = (property) => {
  if (!property) return 0;
  if (property.siteArea)     return parseFloat(property.siteArea)     || 0;
  if (property.buildupArea)  return parseFloat(property.buildupArea)  || 0;
  if (property.site_area)    return parseFloat(property.site_area)    || 0;
  if (property.buildup_area) return parseFloat(property.buildup_area) || 0;
  if (property.max_acres)    return parseFloat(property.max_acres)    || 0;
  if (property.area) {
    return parseFloat(String(property.area).replace(/[^0-9.]/g, '')) || 0;
  }
  return 0;
};

// ─── Price per unit (₹ / sft or ₹ / acre etc.) ────────────────────────────────
const getPricePerUnit = (property) => {
  if (!property?.price) return null;

  const price = parseFloat(String(property.price).replace(/[^0-9.]/g, ''));
  if (!price || price <= 0) return null;

  let area = 0;
  let rawUnit = property.units || '';
  let areaField = 'site_area';

  if (property.buildupArea || property.buildup_area) {
    area = parseFloat(property.buildupArea || property.buildup_area) || 0;
    const floors = parseInt(property.no_of_flores || property.floor || 0, 10);
    if (floors > 0) area = area * floors;
    rawUnit = 'sft';
    areaField = 'buildup_area';
  }
  else if (property.siteArea || property.site_area || property.max_acres) {
    area = parseFloat(property.siteArea || property.site_area || property.max_acres) || 0;
    areaField = property.max_acres ? 'max_acres' : 'site_area';
  }
  else if (property.area) {
    area = parseFloat(String(property.area).replace(/[^0-9.]/g, '')) || 0;
  }

  if (!area || area <= 0) return null;

  return {
    value: Math.round(price / area),
    unit: normalizeUnit(rawUnit, areaField),
  };
};

function PropertyCardImpl({ property, selected, onClick, onView, onWishlistToggle = () => false, isSaved: isSavedProp,
   toggleSave: toggleSaveProp , showAlert, activityType  }) {
  const { userId, isGuest } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [featureRequested, setFeatureRequested] = useState('');
  const [userPlan, setUserPlan] = useState(null);
  const [userActivities, setUserActivities] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  
  const [userType] = useState('Rent');  
  
  useEffect(() => {
    const fetchUserPlan = async () => {
      if (!userId || !userType) {
        setUserPlan(null);
        return;
      }
      try {
        const response = await axios.get(`${BASE_URL}/Get-user-plan/${userId}/`);
        const plans = response.data;
        const activePlan = Array.isArray(plans)
          ? plans.find(plan => plan.user_type === userType)
          : plans.user_type === userType ? plans : null;
        setUserPlan(activePlan);
      } catch (error) {
        console.error('Error fetching user plan:', error);
        setUserPlan(null);
      }
    };
    fetchUserPlan();
  }, [userId, userType]);

  // Load wishlist
  useEffect(() => {
    const loadWishlist = async () => {
      if (!userId) return;
      try {
        const response = await fetch(`${BASE_URL}/get-user-cart/${userId}/`);
        const data = await response.json();
        setWishlistItems(data);
      } catch (error) {
        console.error('Error loading wishlist:', error);
      }
    };
    loadWishlist();
  }, [userId]);

  // Fetch user activities
  useEffect(() => {
    const fetchUserActivities = async () => {
      if (!userId) return;
      try {
        const response = await fetch(`${BASE_URL}/activities/`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const allActivities = await response.json();
        const filteredActivities = allActivities.filter(activity =>
          activity && activity.user_id && String(activity.user_id) === String(userId)
        );
        setUserActivities(filteredActivities);
      } catch (error) {
        console.error('Error fetching activities:', error);
        setUserActivities([]);
      }
    };
    fetchUserActivities();
  }, [userId]);

  // Fetch images
  useEffect(() => {
    const fetchImages = async () => {
      if (!property || !property.id) {
        setLoadingImages(false);
        return;
      }
      
      setLoadingImages(true);
      try {
        const response = await getPropertyImages(property.id);
        if (response.success && response.images?.length > 0) {
          const firstImage = response.images[0];
          const imageUrl = firstImage.startsWith('http')
            ? firstImage
            : `${BASE_URL}/${firstImage.replace(/^\/+/, '')}`;
          setImages([imageUrl]);
        } else {
          setImages(['/default-property-image.jpg']);
        }
      } catch (error) {
        console.error(`Error fetching images for property ${property.id}:`, error);
        setImages(['/default-property-image.jpg']);
      } finally {
        setLoadingImages(false);
      }
    };
    
    fetchImages();
  }, [property.id]);

  const displayImages = images.length > 0 ? images : ['/default-property-image.jpg'];

  // Helper: Check if user is guest and show modal
  const checkGuestAndShowModal = (actionCallback) => {
    if (isGuest) {
      setShowGuestModal(true);
      return false;
    }
    return actionCallback();
  };

  // Check property limit
  const checkPropertyLimit = (currentPropertyId) => {
    if (userPlan?.no_of_properties_unlimited) return true;
    
    const alreadyAccessed = userActivities.some(
      activity => activity.property_id === currentPropertyId && activity.activity_as === userType
    );
    if (alreadyAccessed) return true;
    
    const relevantActivities = userActivities.filter(activity => activity.activity_as === userType);
    const uniqueProperties = new Set(relevantActivities.map(a => a.property_id));
    const propertyLimit = userPlan?.no_of_properties || 10;
    
    if (uniqueProperties.size >= propertyLimit) {
      setShowLimitModal(true);
      return false;
    }
    return true;
  };

  // Log activity to backend
  const logActivity = async (activityType) => {
    try {
      const existingActivity = userActivities.find(
        activity => activity.property_id === property.id && 
                    activity.activity_as === userType
      );

      if (existingActivity) {
        console.log('Activity already exists for this property');
        return true;
      }

      let propertyOwner = property.owner_id ?? property.user_id ?? null;

      if (!propertyOwner) {
        try {
          const propertyResponse = await fetch(`${BASE_URL}/property/${property.id}/`);
          if (propertyResponse.ok) {
            const propertyData = await propertyResponse.json();
            propertyOwner = propertyData.user_id ?? propertyData.owner ?? propertyData.posted_by ?? null;
          }
        } catch (fetchErr) {
          console.error('Failed to fetch property owner:', fetchErr);
        }
      }

      const activityData = {
        user_id: userId,
        property_id: property.id,
        activity_type: activityType,
        property_by: propertyOwner,
        activity_as: userType || "Rent"  
      };

      const response = await fetch(`${BASE_URL}/activities/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activityData)
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.log("Backend Error:", errorData);
        throw new Error('Failed to log activity');
      }

      const newActivity = await response.json();
      setUserActivities(prev => [...prev, newActivity]);
      return true;
    } catch (err) {
      console.error("Error logging activity:", err);
      return false;
    }
  };

  // Handle any action with subscription and limit checks
  const handleAction = async (e, actionName, actionCallback, activityType) => {
    e.stopPropagation();

    if (isGuest) {
      setShowGuestModal(true);
      return;
    }

    const alreadyAccessed = userActivities.some(
      activity => activity.property_id === property.id && activity.activity_as === userType
    );

    if (!alreadyAccessed) {
      const withinLimit = checkPropertyLimit(property.id);
      if (!withinLimit) return;
    }

    try {
      await actionCallback();
      if (activityType && !alreadyAccessed) await logActivity(activityType);
    } catch (error) {
      console.error(`Error in ${actionName} action:`, error);
    }
  };

  // Handle location click
  const handleLocationClick = (e) => {
    handleAction(e, "Location", async () => {
      setLoading(true);

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      const openInMaps = (url) => {
        setLoading(false);
        window.location.href = url;
      };

      const fallbackUrl = isIOS
        ? `http://maps.apple.com/?q=${encodeURIComponent(property.location || '')}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.location || `${property.lat},${property.lng}`)}`;

      if (property.lat && property.lng && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLat = position.coords.latitude;
            const userLng = position.coords.longitude;
            const mapsUrl = isIOS
              ? `http://maps.apple.com/?daddr=${property.lat},${property.lng}`
              : `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${property.lat},${property.lng}&travelmode=driving`;
            openInMaps(mapsUrl);
          },
          (error) => {
            console.error("Error getting user location:", error);
            openInMaps(fallbackUrl);
          },
          { timeout: 10000 }
        );
      } else {
        openInMaps(fallbackUrl);
      }
    }, "Location");
  };

  const handleWishlistClick = async (e) => {
    e.stopPropagation();

    if (isGuest) {
      setShowGuestModal(true);
      return;
    }

    if (wishlistLoading) return;

    const isSaved = wishlistItems.some(item => item.property_id === property.id);
    
    if (isSaved) {
      setWishlistItems(prev => prev.filter(item => item.property_id !== property.id));
    } else {
      setWishlistItems(prev => [...prev, {
        cart_id: wishlistItems.length + 1,
        property_id: property.id,
        user_id: userId
      }]);
    }
    
    setWishlistLoading(true);
    
    try {
      if (isSaved) {
        const cartItem = wishlistItems.find(item => item.property_id === property.id);
        if (cartItem && cartItem.cart_id) {
          await fetch(`${BASE_URL}/user-cart/${cartItem.cart_id}/`, {
            method: 'DELETE',
          });
        }
        
        if (onWishlistToggle) {
          onWishlistToggle(property);
        }
      } else {
        const response = await fetch(`${BASE_URL}/user-cart/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            user_id: userId, 
            property_id: property.id 
          }),
        });
        const data = await response.json();
        
        if (response.ok && data && data.cart_id) {
          setWishlistItems(prev => 
            prev.map(item => 
              item.property_id === property.id 
                ? { ...item, cart_id: data.cart_id }
                : item
            )
          );
        }
        
        if (onWishlistToggle) {
          onWishlistToggle(property);
        }
        
        await logActivity("Liked");
      }
    } catch (error) {
      console.error('Wishlist error:', error);
      if (isSaved) {
        const originalItem = wishlistItems.find(item => item.property_id === property.id);
        if (originalItem) {
          setWishlistItems(prev => [...prev, originalItem]);
        }
      } else {
        setWishlistItems(prev => prev.filter(item => item.property_id !== property.id));
      }
    } finally {
      setWishlistLoading(false);
    }
  };

 const handleCardClick = async (e) => {
  e.stopPropagation();

  if (isGuest) {
    setShowGuestModal(true);
    return;
  }

  const alreadyAccessed = userActivities.some(
    activity =>
      activity.property_id === property.id &&
      activity.activity_as === userType
  );

  if (!alreadyAccessed) {
    const withinLimit = checkPropertyLimit(property.id);
    if (!withinLimit) return;

    try {
      await logActivity("Description");
    } catch (error) {
      console.error('Error logging description view activity:', error);
    }
  }

  localStorage.setItem('lastViewedPropertyId', property.id);

  // Card click → Lease Description
  navigate(`/lease-description/${property.id}`);
};
// ── Computed price values ───────────────────────────────────────────────────
const pricePerUnit = getPricePerUnit(property);
const totalArea = getAreaNumber(property);

// ── Safe location parts ─────────────────────────────────────────────────────
const locationParts = (property.location || '').split(',').map(s => s.trim()).filter(Boolean);
const localityLine = property.locality || property.area || locationParts[0] || 'Location';
const cityLine = property.city || property.state || locationParts.slice(1).join(', ') || '';
  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % displayImages.length);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + displayImages.length) % displayImages.length);
  };

  const isPropertySaved = wishlistItems.some(item => item.property_id === property.id);

  return (
    <>
      <Card
        onClick={handleCardClick}
        sx={{
          display: "flex",
          flexDirection: "row",
          width: '100%',
          height: 135,
          maxWidth: 290,
          cursor: 'pointer',
          borderRadius: 2,
          overflow: 'hidden',
          transform: selected ? 'translateY(-2px)' : 'none',
          transition: 'transform 200ms, border-color 200ms',
          boxShadow: selected ? '0 4px 12px rgba(0,0,0,0.5)' : '0 2px 6px rgba(0,0,0,0.3)',
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        {/* Left Side: Image with Carousel */}
        <Box
          sx={{
            position: "relative",
            width: "38%",
            height: "100%",
          }}
        >
          {loadingImages ? (
            <Box sx={{ 
              width: "100%", 
              height: "100%", 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: '#f5f5f5',
              borderRadius: 2
            }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <CardMedia
              component="img"
              image={displayImages[currentImageIndex]}
              alt="Property"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
              }}
            />
          )}

          {/* Carousel Navigation Arrows */}
          {displayImages.length > 1 && !loadingImages && (
            <>
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 4,
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.8)" },
                  p: 0.3,
                  width: 22,
                  height: 22
                }}
                onClick={handlePrevImage}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 4,
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.8)" },
                  p: 0.3,
                  width: 22,
                  height: 22
                }}
                onClick={handleNextImage}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </>
          )}

          {/* Carousel Indicators */}
          {displayImages.length > 1 && !loadingImages && (
            <Box
              sx={{
                position: "absolute",
                bottom: 6,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "4px",
              }}
            >
              {displayImages.map((_, index) => (
                <Box
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: currentImageIndex === index ? "#1976d2" : "#ccc",
                    cursor: "pointer",
                  }}
                />
              ))}
            </Box>
          )}

          {/* Wishlist Icon */}
          <Tooltip title={isPropertySaved ? "Remove from Wishlist" : "Add to Wishlist"}>
            <IconButton
              sx={{
                position: "absolute",
                top: 7,
                right: 3,
                bgcolor: "white",
                boxShadow: 1,
                p: 0.5,
                width: 24,
                height: 24,
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
              onClick={handleWishlistClick}
              disabled={wishlistLoading}
            >
              {wishlistLoading ? (
                <CircularProgress size={16} />
              ) : isPropertySaved ? (
                <Favorite color="error" fontSize="small" />
              ) : (
                <FavoriteBorder fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <CardContent
          sx={{
            width: "62%",
            p: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: 135,
            boxSizing: 'border-box'
          }}
        >
          {/* Title */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.2 }}>
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
                lineHeight: 1.2
              }}
            >
              {property.title || property.categoryName || "Property"}
            </Typography>
          </Box>

    {/* Price / Per <unit>  |  Total Price */}
<Box sx={{ display: 'flex', alignItems: 'center', mt: 0.1, mb: 0.2 }}>
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.5rem', color: '#888', lineHeight: 1.1 }}>
      Price / Per {pricePerUnit?.unit || 'sft'}
    </Typography>
    <Typography
                      fontWeight="bold"
                      sx={{
                        fontSize: '0.68rem',
                        color: '#8B4F16',
                        lineHeight: 1.2,
                        fontWeight: 900
                      }}
                    >
                    ₹{pricePerUnit ? formatPrice(pricePerUnit.value) : 'NA'}
                         <Typography
                           component="span"
                           sx={{ fontSize: '0.5rem', color: '#888', fontWeight: 400 }}
                         >
                           {' '}/{pricePerUnit?.unit || 'sft'}
                         </Typography>
                       </Typography>
  </Box>

  <Box sx={{ width: '1px', height: 26, bgcolor: '#e0e0e0', mx: 0.6 }} />

  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
    <Typography sx={{ fontSize: '0.5rem', color: '#888', lineHeight: 1.1 }}>
      Total Price
    </Typography>
    <Typography fontWeight="bold" sx={{ fontSize: '0.7rem', lineHeight: 1.2, color: '#1F1F1F' }}>
      ₹{formatPrice(property.price)}
    </Typography>
  </Box>
</Box>

<Box
  // onClick={handleLocationClick}
  role="button"
  tabIndex={0}
  sx={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0.5,
    mt: 0.1,
    mb: 0.1,
    cursor: 'pointer',
    borderRadius: 1,
    px: 0.3,
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: 'rgba(201,138,44,0.10)',
    },
    '&:active': {
      backgroundColor: 'rgba(201,138,44,0.18)',
    },
  }}
>
  <LocationOn
    sx={{
      fontSize: '0.9rem',
      color: '#8B4F16',
      mt: 0.1,
      flexShrink: 0,
    }}
  />

  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
    <Typography
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,             
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',         
        color: '#5D4037',
fontSize: { xs: '0.62rem', sm: '0.72rem' },
        lineHeight: 1.2,              
        fontWeight: 500,
      }}
    >
      {property.location || 'Location not available'}
    </Typography>
  </Box>
</Box>

          {/* Property Info Grid */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0.2,
              mb: 0.2,
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              overflow: "hidden",
              minHeight: 26,
            }}
          >
            <Box sx={{ flex: 1.2, minWidth: 0, textAlign: "center", p: 0.4, borderRight: "1px solid #e0e0e0" }}>
              <Typography fontWeight="bold" sx={{ fontSize: "0.55rem", lineHeight: 1.1 }}>
                {property.facing ? property.facing.split("-")[0] : "N/A"}
              </Typography>
              <Typography sx={{ fontSize: "0.5rem", lineHeight: 1.1, color: '#666' }}>
                {property.facing?.split("-")[1] || ""}
              </Typography>
            </Box>

            <Box sx={{ flex: 1.4, minWidth: 0, textAlign: "center", p: 0.4, borderRight: "1px solid #e0e0e0" }}>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: "0.55rem",
                  lineHeight: 1.1,
                  maxWidth: "60px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {property.siteArea || property.buildupArea || property.area || "N/A"}
              </Typography>
              {property.units && (
                <Typography sx={{ fontSize: "0.5rem", lineHeight: 1.1, color: "#666" }}>
                  {["ft", "m", "cm", "in"].includes(property.units.toLowerCase()) &&
                  !property.units.toLowerCase().startsWith("sq.")
                    ? `sq.${property.units}`
                    : property.units}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, textAlign: "center", p: 0.3 }}>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: "0.55rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.1
                }}
              >
                {property.postedBy || "Agent"}
              </Typography>
            </Box>
          </Box>

          {/* View Details Button */}
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={handleCardClick}
            sx={{
              mt: 0.2,
              py: 0.18,
              fontSize: "0.7rem",
              fontWeight: "600",
              bgcolor: "#8B4F16",
              color: "#fff",
              borderRadius: "2px",
              '&:hover': { bgcolor: "#f5f5f5" },
              textTransform: "none",
              minHeight: 22,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1.5
            }}
          >
            View Details
            <ArrowForward sx={{ fontSize: "0.9rem" }} />
          </Button>
        </CardContent>
      </Card>

      {/* Subscribe Modal */}
      {showSubscribeModal && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Paper sx={{ padding: 2, maxWidth: 250, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Subscription Required</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Please subscribe to use the {featureRequested} feature.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setShowSubscribeModal(false)}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setShowSubscribeModal(false);
                  navigate('/user-subscription');
                }}
                size="small"
              >
                Subscribe
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Limit Exceeded Modal */}
      {showLimitModal && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Paper sx={{ padding: 2, maxWidth: 250, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Limit Exceeded</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {userPlan?.no_of_properties_unlimited
                ? "You have unlimited access to properties."
                : `You've reached your limit of ${userPlan?.no_of_properties || 10} properties.
                 Please upgrade your subscription plan for more.`}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowLimitModal(false)}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setShowLimitModal(false);
                  navigate('/user-subscription');
                }}
                size="small"
              >
                Upgrade
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Guest Login Modal */}
      <GuestLoginModal
        open={showGuestModal}
        onClose={() => {
          setShowGuestModal(false);
        }}
      />
    </>
  );
}

export default memo(PropertyCardImpl, (prevProps, nextProps) => {
  return prevProps.property?.id === nextProps.property?.id && 
         prevProps.selected === nextProps.selected &&
         prevProps.isSaved?.(prevProps.property) === nextProps.isSaved?.(nextProps.property);
});